import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Utensils, Plus, Users, Trash2, Edit3 } from "lucide-react";

const STATUS = {
  free: { l: "شاغرة", c: "bg-emerald-500 hover:bg-emerald-600 text-white" },
  occupied: { l: "مشغولة", c: "bg-red-500 hover:bg-red-600 text-white" },
  reserved: { l: "محجوزة", c: "bg-amber-500 hover:bg-amber-600 text-white" },
  cleaning: { l: "تنظيف", c: "bg-slate-400 hover:bg-slate-500 text-white" },
};

export default function RestaurantTables() {
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ number: "", capacity: 2, location: "", status: "free" });

  const load = async () => { try { const { data } = await api.get("/restaurant/tables"); setItems(data); } catch (e) { toast.error(formatErr(e)); } };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ number: "", capacity: 2, location: "قاعة رئيسية", status: "free" }); setDlg(true); };
  const openEdit = (t) => { setEditing(t); setForm({ number: t.number, capacity: t.capacity, location: t.location || "", status: t.status }); setDlg(true); };

  const save = async () => {
    if (!form.number.trim()) return toast.error("رقم الطاولة مطلوب");
    try {
      if (editing) await api.put(`/restaurant/tables/${editing.id}`, form);
      else await api.post("/restaurant/tables", form);
      toast.success("تم الحفظ"); setDlg(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (t) => {
    if (!window.confirm(`حذف الطاولة ${t.number}؟`)) return;
    try { await api.delete(`/restaurant/tables/${t.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const setStatus = async (t, status) => {
    try { await api.post(`/restaurant/tables/${t.id}/status`, { status }); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const stats = { free: 0, occupied: 0, reserved: 0, cleaning: 0 };
  items.forEach(t => { stats[t.status] = (stats[t.status] || 0) + 1; });

  return (
    <div className="space-y-6" data-testid="tables-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><Utensils className="w-6 h-6 text-primary" /> إدارة الطاولات</h1>
          <p className="text-sm text-muted-foreground mt-1">تنظيم قاعة المطعم وحالة كل طاولة</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="new-table-btn"><Plus className="w-4 h-4" /> طاولة جديدة</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(STATUS).map(([k, v]) => (
          <div key={k} className="p-4 rounded-lg border bg-card">
            <div className="text-xs text-muted-foreground">{v.l}</div>
            <div className={`text-2xl font-bold mt-1 ${k === "free" ? "text-emerald-600" : k === "occupied" ? "text-red-600" : k === "reserved" ? "text-amber-600" : "text-slate-500"}`}>{stats[k] || 0}</div>
          </div>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed rounded-lg text-muted-foreground">
          <Utensils className="w-16 h-16 mx-auto opacity-30 mb-3" />
          <div>لا توجد طاولات — أضف الطاولة الأولى</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {items.map(t => (
            <div key={t.id} className="border rounded-lg p-3 bg-card space-y-2" data-testid={`table-${t.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-bold text-lg">{t.number}</div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(t)} className="text-muted-foreground hover:text-primary" data-testid={`edit-${t.id}`}><Edit3 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => del(t)} className="text-muted-foreground hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-1"><Users className="w-3 h-3" /> {t.capacity}</div>
              {t.location && <div className="text-xs text-muted-foreground truncate">{t.location}</div>}
              <div className="grid grid-cols-2 gap-1">
                {Object.entries(STATUS).map(([k, v]) => (
                  <button key={k} onClick={() => setStatus(t, k)} className={`text-[10px] px-1.5 py-1 rounded transition ${t.status === k ? v.c : "bg-muted hover:bg-muted-foreground/10"}`} data-testid={`status-${t.id}-${k}`}>{v.l}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent data-testid="table-dialog">
          <DialogHeader><DialogTitle>{editing ? "تعديل طاولة" : "طاولة جديدة"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>رقم/اسم الطاولة</Label><Input value={form.number} onChange={e => setForm({ ...form, number: e.target.value })} placeholder="T1, T2, VIP-1..." data-testid="table-number" /></div>
            <div><Label>السعة (عدد الأشخاص)</Label><Input type="number" value={form.capacity} onChange={e => setForm({ ...form, capacity: parseInt(e.target.value) || 0 })} /></div>
            <div><Label>المكان</Label><Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="قاعة رئيسية / شرفة / خارجي" /></div>
            <div><Label>الحالة</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlg(false)}>إلغاء</Button><Button onClick={save} data-testid="save-table-btn">حفظ</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
